import { usePlatform } from "@/components/platform/PlatformProvider";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { APP_STORE_URL, PLAY_STORE_URL } from "@workspace/app-store-destinations";
import eventsAppIcon from "@/assets/app-icon-events.webp";
import driveshareAppIcon from "@/assets/app-icon-driveshare.webp";

// Footer-only destinations; other promotions retain their shared listings.
const downloads = [
  {
    id: "events",
    headingKey: "footer.get_events_app",
    icon: eventsAppIcon,
    ios: "https://apps.apple.com/gb/app/driveshare-events/id6752524927",
    android: "https://play.google.com/store/apps/details?id=co.dsevents.android",
  },
  {
    id: "driveshare",
    headingKey: "footer.get_driveshare_app",
    icon: driveshareAppIcon,
    ios: "https://itunes.apple.com/us/app/driveshare-by-hagerty/id1031581546",
    android: "https://play.google.com/store/apps/details?id=com.driveshare.app.android.driveshare",
  },
] as const;

const badgeFocus = "inline-flex min-h-11 min-w-11 items-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white";

export function FooterAppDownloads({ ios, android }: { ios: boolean; android: boolean }) {
  const { platform, name } = usePlatform();
  const { t } = useAppTranslation();
  const base = import.meta.env.BASE_URL;

  if (!ios && !android) return null;

  if (platform === "bac") {
    return (
      <div className="mt-6 flex gap-3">
        {ios && (
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label={t("footer.app_store_badge_alt")} className={badgeFocus}>
            <img src={`${base}images/app-store-badge.png`} alt={t("footer.app_store_badge_alt")} className="h-10" loading="lazy" />
          </a>
        )}
        {android && (
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label={t("footer.google_play_badge_alt")} className={badgeFocus}>
            <img src={`${base}images/google-play-badge.png`} alt={t("footer.google_play_badge_alt")} className="h-10" loading="lazy" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="mt-6 min-w-0 space-y-6">
      {downloads.map((app) => {
        const appName = app.id === "events" ? `${name} Events` : name;
        const heading = t(app.headingKey, { app: name });
        return (
          <section key={app.id} aria-label={heading}>
            <div className="flex items-center gap-3">
              <img src={app.icon} alt="" width={40} height={40} className="h-10 w-10 shrink-0 rounded-lg border border-white/30 object-contain" loading="lazy" />
              <h3 className="text-sm font-semibold uppercase tracking-widest text-white/80">{heading}</h3>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              {ios && (
                <a href={app.ios} target="_blank" rel="noopener noreferrer" aria-label={t("footer.app_download_ios", { app: appName })} className={badgeFocus}>
                  <img src={`${base}images/app-store-badge.png`} alt={t("footer.app_store_badge_alt")} className="h-9 w-auto object-contain" loading="lazy" />
                </a>
              )}
              {android && (
                <a href={app.android} target="_blank" rel="noopener noreferrer" aria-label={t("footer.app_download_android", { app: appName })} className={badgeFocus}>
                  <img src={`${base}images/google-play-badge.png`} alt={t("footer.google_play_badge_alt")} className="h-9 w-auto object-contain" loading="lazy" />
                </a>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
